<template>
  <div class="qr-scanner">
    <h4 v-if="title" class="mt-5 mb-3">{{ title }}</h4>

    <div class="d-flex flex-column align-items-center">
      <div
        class="view-finder-wrapper d-flex justify-content-center align-items-center"
        :class="{ 'view-finder-wrapper--inactive': !cameraActivated }"
        @click="!cameraActivated && toggleCamera()"
      >
        <div class="loader" v-if="cameraActivated && initializing"></div>

        <img v-if="!cameraActivated" class="camera-off-icon" src="../assets/icons/camera.svg" width="40" height="40" alt="" />

        <qrcode-stream
          v-if="!destroyed && cameraActivated"
          :camera="camera"
          class="view-finder"
          @decode="onDecode"
          @init="onInit"
        ></qrcode-stream>

        <button
          v-if="cameraActivated"
          type="button"
          class="flip-camera-button"
          aria-label="Switch camera"
          @click="flipCamera"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17 2l3 3-3 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M3 12a9 9 0 0 1 9-9c3.5 0 6 1.5 8 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M7 22l-3-3 3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M21 12a9 9 0 0 1-9 9c-3.5 0-6-1.5-8-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>

      <button type="button" class="activate-camera-cta" @click="toggleCamera">
        <img src="../assets/icons/camera.svg" width="18" height="18" alt="" />
        <span>{{ cameraActivated ? 'Stop camera' : 'Tap to activate camera' }}</span>
      </button>

      <p class="qr-scanner-error" v-if="error">{{ error }}</p>
    </div>
  </div>
</template>

<script>
import { QrcodeStream } from 'vue-qrcode-reader';
import { installCameraDeviceShim, uninstallCameraDeviceShim } from '../utils/cameraDeviceShim';

export default {
  name: 'QrScanner',
  components: {
    QrcodeStream,
  },
  props: {
    title: {
      type: String,
      default: 'Scan QR code',
    },
  },
  data: function () {
    return {
      camera: 'auto',
      error: '',
      initializing: false,
      destroyed: false,
    };
  },
  // Installed in created() rather than mounted(): Vue mounts children before the
  // parent's mounted hook, so a truthy cameraActivated on first render would let
  // qrcode-stream initialise the camera before the shim was in place.
  created() {
    installCameraDeviceShim();
  },
  destroyed() {
    uninstallCameraDeviceShim();
  },
  computed: {
    cameraActivated() {
      return this.$store.getters.getCameraActivated;
    },
  },
  methods: {
    onDecode(decodedString) {
      this.$emit('decode', decodedString);
      this.reload();
    },
    flipCamera() {
      // Changing `camera` alone is enough - qrcode-stream watches this prop
      // and re-initializes itself. Also calling reload() here raced two
      // overlapping camera inits against each other.
      // Starts from 'auto' (picks whatever back camera is available, or the
      // only camera on single-camera devices), then toggles front/rear once
      // the user has manually flipped at least once.
      this.camera = this.camera === 'front' ? 'rear' : 'front';
    },
    async reload() {
      this.destroyed = true;
      await this.$nextTick();
      this.destroyed = false;
    },
    toggleCamera() {
      this.$store.commit('SET_CAMERA_ACTIVATED', !this.cameraActivated);
    },
    async onInit(promise) {
      this.error = '';
      this.initializing = true;
      try {
        await promise;
      } catch (error) {
        if (error.name === 'NotAllowedError') {
          this.error = 'ERROR: you need to grant camera access permisson';
        } else if (error.name === 'NotFoundError') {
          this.error = 'ERROR: no camera on this device';
        } else if (error.name === 'NotSupportedError') {
          this.error = 'ERROR: secure context required (HTTPS, localhost)';
        } else if (error.name === 'NotReadableError') {
          this.error = 'ERROR: is the camera already in use?';
        } else if (error.name === 'OverconstrainedError') {
          this.error = 'ERROR: installed cameras are not suitable';
          // A specific front/rear request can fail on devices that don't
          // expose that facingMode (e.g. a single laptop webcam). Fall back
          // to 'auto' so the camera self-recovers instead of staying broken.
          if (this.camera !== 'auto') {
            this.camera = 'auto';
          }
        } else if (error.name === 'StreamApiNotSupportedError') {
          this.error = 'ERROR: Stream API is not supported in this browser';
        } else {
          this.error = `ERROR: ${error.name || 'unknown'} - ${error.message || 'no message'}`;
        }
      } finally {
        this.initializing = false;
      }
    },
  },
};
</script>

<style scoped>
.loader {
  position: absolute;
}
</style>
